/**
 * Offline Cache Utility
 * Uses IndexedDB for reliable offline caching (localStorage had quota issues).
 * Each cache entry stores: { data, timestamp }
 * Default TTL: 7 days (data is still shown if expired, but refreshed when online)
 */

const DB_NAME = 'fd_cache';
const DB_VERSION = 1;
const STORE_NAME = 'cache';
const DEFAULT_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

/** Open (or create) the IndexedDB database */
function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Save data to cache (async, fire-and-forget safe)
 * @param {string} key - Cache key (e.g. 'wardrobe_items')
 * @param {any} data - Data to cache
 */
export async function cacheSet(key, data) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put({ data, timestamp: Date.now() }, key);
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
    console.log('✅ Cached:', key);
  } catch (err) {
    console.warn('⚠️ Cache write failed:', key, err.message);
  }
}

/**
 * Get data from cache
 * @param {string} key - Cache key
 * @param {number} ttl - Max age in ms (default 7 days). Returns data even if expired (for offline use).
 * @returns {Promise<{ data: any, isStale: boolean } | null>}
 */
export async function cacheGet(key, ttl = DEFAULT_TTL) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(key);
    const entry = await new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    return { data: entry.data, isStale: age > ttl };
  } catch {
    return null;
  }
}

/**
 * Remove a specific cache entry
 */
export async function cacheRemove(key) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(key);
  } catch { /* ignore */ }
}

/**
 * Clear all cache entries (e.g. on logout)
 */
export async function cacheClearAll() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
  } catch { /* ignore */ }

  // Also clean up any leftover localStorage cache entries from old implementation
  try {
    Object.keys(localStorage)
      .filter(k => k.startsWith('fd_cache_'))
      .forEach(k => localStorage.removeItem(k));
  } catch { /* ignore */ }
}
